/**
 * 플레어 헤더 배압 검토 화면 (2단계 — 망 구조 + 동시 분출 조합).
 *
 * 화면을 세 덩어리로 나눈다.
 *   ① 시나리오 — 어떤 조합을 보고 있나, 조합끼리 비교하면 어떤가
 *   ② 결과 — 지금 보는 조합의 배압·마하수
 *   ③ 배관 — 밸브와 헤더 구간의 사양 (조합이 바뀌어도 그대로다)
 */
import { useMemo } from "react";
import { FITTINGS, FITTING_KINDS, type FittingKind } from "../calc/fittings";
import { PIPE_ROUGHNESS_M } from "../calc/pipeFlow";
import { PIPE_SIZES, nearestSchedule, schedulesFor } from "../data/pipes";
import { Field, Help, Item } from "./formBits";
import { gaugeLabel, type PressureUnit } from "./pressureUnit";
import {
  addFitting,
  MATERIAL_LABELS,
  setFittingCount,
  type PipeMaterial,
} from "./pipingState";
import {
  compareScenarios,
  findScenario,
  newFlareScenario,
  newFlareSegment,
  newFlareValve,
  runFlare,
  scenarioFlow,
  type FlarePipeSpec,
  type FlareScenarioState,
  type FlareState,
  type FlareValveState,
} from "./flareState";

const nf = (v: number, d = 1) =>
  Number.isFinite(v)
    ? v.toLocaleString("ko-KR", {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
      })
    : "—";

/** 배관 사양 입력 한 덩어리. 헤더 구간과 테일파이프가 공유한다. */
function PipeSpecForm({
  value,
  onChange,
}: {
  value: FlarePipeSpec;
  onChange: (next: FlarePipeSpec) => void;
}) {
  const set = <K extends keyof FlarePipeSpec>(k: K, v: FlarePipeSpec[K]) =>
    onChange({ ...value, [k]: v });

  return (
    <>
      <div className="grid-form">
        <Field label="호칭경" unit="in" help="pipeNps">
          <select
            value={value.nps}
            // 호칭경을 바꾸면 그 크기에 없는 스케줄이 남을 수 있다.
            onChange={(e) =>
              onChange({
                ...value,
                nps: e.target.value,
                schedule: nearestSchedule(e.target.value, value.schedule),
              })
            }
            disabled={value.useCustomId}
          >
            {PIPE_SIZES.map((s) => (
              <option key={s.nps} value={s.nps}>
                {s.nps}&quot;
              </option>
            ))}
          </select>
        </Field>
        <Field label="스케줄" unit="" help="pipeSchedule">
          <select
            value={value.schedule}
            onChange={(e) => set("schedule", e.target.value)}
            disabled={value.useCustomId}
          >
            {schedulesFor(value.nps).map((s) => (
              <option key={s} value={s}>
                Sch {s}
              </option>
            ))}
          </select>
        </Field>
        <Field label="직관 길이" unit="m" help="straightLength">
          <input
            value={value.straightLengthM}
            onChange={(e) => set("straightLengthM", e.target.value)}
          />
        </Field>
        <Field label="재질·조도" unit="" help="pipeMaterial">
          <select
            value={value.material}
            onChange={(e) => set("material", e.target.value as PipeMaterial)}
          >
            {(Object.keys(PIPE_ROUGHNESS_M) as PipeMaterial[]).map((m) => (
              <option key={m} value={m}>
                {MATERIAL_LABELS[m]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="부속 여유율" unit="%" help="fittingAllowance">
          <input
            value={value.fittingAllowancePercent}
            placeholder="예: 100"
            onChange={(e) => set("fittingAllowancePercent", e.target.value)}
          />
        </Field>
        {value.useCustomId && (
          <Field label="내경 직접 입력" unit="mm" help="customInnerDiameter">
            <input
              value={value.customIdMm}
              onChange={(e) => set("customIdMm", e.target.value)}
            />
          </Field>
        )}
      </div>

      <label className="piperun__custom">
        <input
          type="checkbox"
          checked={value.useCustomId}
          onChange={(e) => set("useCustomId", e.target.checked)}
        />
        내경 직접 입력
        <Help topic="customInnerDiameter" />
      </label>

      <div className="flare__fittings">
        <select
          value=""
          onChange={(e) => {
            const k = e.target.value as FittingKind;
            if (k) set("fittings", addFitting(value.fittings, k));
          }}
        >
          <option value="">+ 부속 추가…</option>
          {FITTING_KINDS.filter(
            (k) => !value.fittings.some((f) => f.kind === k),
          ).map((k) => (
            <option key={k} value={k}>
              {FITTINGS[k].label}
            </option>
          ))}
        </select>
        {value.fittings.map((f) => (
          <span className="flare__fitting" key={f.kind}>
            {FITTINGS[f.kind].label}
            <input
              className="cell-input"
              type="number"
              min={0}
              value={f.count}
              onChange={(e) =>
                set(
                  "fittings",
                  setFittingCount(value.fittings, f.kind, Number(e.target.value)),
                )
              }
            />
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() =>
                set(
                  "fittings",
                  value.fittings.filter((x) => x.kind !== f.kind),
                )
              }
            >
              ×
            </button>
          </span>
        ))}
      </div>
    </>
  );
}

export function FlarePanel({
  value,
  unit,
  onChange,
  onApplyBackpressure,
}: {
  value: FlareState;
  unit: PressureUnit;
  onChange: (next: FlareState) => void;
  /**
   * 밸브별 최악 배압을 케이스 검토로 보낸다.
   * 태그가 맞는 밸브를 찾아 배압 칸을 채운다.
   */
  onApplyBackpressure: (
    entries: ReadonlyArray<{ tag: string; backpressureKpaGauge: number }>,
  ) => void;
}) {
  const patch = (p: Partial<FlareState>) => onChange({ ...value, ...p });
  const out = useMemo(() => runFlare(value, unit), [value, unit]);
  const comparison = useMemo(() => compareScenarios(value, unit), [value, unit]);
  const r = out.result;

  const active = findScenario(value, value.activeScenarioId);
  const derivedOf = (id: string) => out.derived.find((d) => d.id === id);

  const patchValve = (id: string, p: Partial<FlareValveState>) =>
    patch({
      valves: value.valves.map((v) => (v.id === id ? { ...v, ...p } : v)),
    });

  const patchScenario = (id: string, p: Partial<FlareScenarioState>) =>
    patch({
      scenarios: value.scenarios.map((s) => (s.id === id ? { ...s, ...p } : s)),
    });

  const setFlow = (scenarioId: string, valveId: string, flow: string) => {
    const sc = findScenario(value, scenarioId);
    if (!sc) return;
    patchScenario(scenarioId, { flows: { ...sc.flows, [valveId]: flow } });
  };

  /** 구간 이름을 id 로 찾는다 — 연결 선택에 쓴다. */
  const segmentLabel = (id: string | null) =>
    id === null
      ? "플레어 팁"
      : (value.segments.find((s) => s.id === id)?.label ?? "?");

  return (
    <>
      <section className="card">
        <h2>플레어 헤더 검토</h2>
        <p className="hint">
          여러 밸브가 헤더로 합류할 때 각 밸브가 받는 배압을 구합니다. 표준이
          정한 대로 <b>등온 압축성 유동식</b>으로 계산하고, 압력을 아는{" "}
          <b>플레어 팁에서 거꾸로 거슬러 올라갑니다</b> (API 521 5.5.5).
          지선이 갈라지는 구조도 다룹니다.
        </p>
        <div className="grid-form">
          <Field label="팁 압력" unit={gaugeLabel(unit)} help="tipPressure">
            <input
              value={value.tipPressure}
              onChange={(e) => patch({ tipPressure: e.target.value })}
            />
          </Field>
          <Field label="마하수 상한" unit="" help="machLimit">
            <input
              value={value.machLimit}
              onChange={(e) => patch({ machLimit: e.target.value })}
            />
          </Field>
        </div>
      </section>

      {/* ── 시나리오 비교 ── */}
      <section className="card">
        <div className="row-head">
          <h2>동시 분출 조합</h2>
          <button
            type="button"
            className="btn"
            onClick={() => {
              const sc = newFlareScenario(
                `시나리오 ${value.scenarios.length + 1}`,
              );
              patch({
                scenarios: [...value.scenarios, sc],
                activeScenarioId: sc.id,
              });
            }}
          >
            + 조합 추가
          </button>
        </div>
        <p className="hint">
          정전과 화재는 함께 터지는 밸브가 다릅니다. 조합마다 유량을 넣고{" "}
          <b>가장 나쁜 조합</b>으로 헤더를 정하십시오 (API 521 5.5.2, 5.5.4).
        </p>

        <div className="flare__tabs">
          {value.scenarios.map((sc) => {
            const sum = comparison.summaries.find((x) => x.scenarioId === sc.id);
            return (
              <button
                key={sc.id}
                type="button"
                className={
                  "tabs__btn" +
                  (sc.id === value.activeScenarioId ? " tabs__btn--active" : "")
                }
                onClick={() => patch({ activeScenarioId: sc.id })}
              >
                {sc.name}
                {sum && !sum.ok && " ⚠"}
              </button>
            );
          })}
        </div>

        {active && (
          <div className="flare__scenario-head">
            <Field label="조합 이름" unit="" help="flareScenario">
              <input
                value={active.name}
                onChange={(e) =>
                  patchScenario(active.id, { name: e.target.value })
                }
              />
            </Field>
            <button
              type="button"
              className="btn btn--ghost"
              disabled={value.scenarios.length <= 1}
              onClick={() => {
                const rest = value.scenarios.filter((s) => s.id !== active.id);
                patch({
                  scenarios: rest,
                  activeScenarioId: rest[0]!.id,
                });
              }}
            >
              이 조합 삭제
            </button>
          </div>
        )}

        {/* 조합별 유량 표 — 한 화면에서 전부 고칠 수 있게 둔다 */}
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>밸브</th>
                {value.scenarios.map((sc) => (
                  <th
                    key={sc.id}
                    className={
                      sc.id === value.activeScenarioId ? "th--active" : undefined
                    }
                  >
                    {sc.name}
                  </th>
                ))}
                <th>최악 배압 (kPag)</th>
                <th>지배 조합</th>
              </tr>
            </thead>
            <tbody>
              {value.valves.map((v) => {
                const worst = comparison.worstPerValve.find(
                  (w) => w.valveId === v.id,
                );
                return (
                  <tr key={v.id}>
                    <td>{v.tag}</td>
                    {value.scenarios.map((sc) => (
                      <td key={sc.id}>
                        <input
                          className="cell-input cell-input--wide"
                          value={scenarioFlow(sc, v.id)}
                          onChange={(e) => setFlow(sc.id, v.id, e.target.value)}
                        />
                      </td>
                    ))}
                    <td>
                      <b>
                        {worst ? nf(worst.backpressureKpaGauge, 1) : "—"}
                      </b>
                    </td>
                    <td className="muted">{worst?.scenarioName ?? "—"}</td>
                  </tr>
                );
              })}
              <tr>
                <td className="muted">총 유량 (kg/h)</td>
                {comparison.summaries.map((sum) => (
                  <td key={sum.scenarioId} className="muted">
                    {sum.ok ? nf(sum.totalFlowKgH, 0) : "계산 불가"}
                  </td>
                ))}
                <td colSpan={2} />
              </tr>
              <tr>
                <td className="muted">헤더 최대 Ma</td>
                {comparison.summaries.map((sum) => (
                  <td
                    key={sum.scenarioId}
                    className={sum.machExceeded ? "cell--bad" : "muted"}
                  >
                    {sum.ok ? nf(sum.maxHeaderMach, 3) : "—"}
                  </td>
                ))}
                <td colSpan={2} />
              </tr>
            </tbody>
          </table>
        </div>

        {comparison.summaries.some((s) => !s.ok) && (
          <ul className="warnings">
            {comparison.summaries
              .filter((s) => !s.ok)
              .map((s) => (
                <li key={s.scenarioId}>
                  [{s.name}] {s.message}
                </li>
              ))}
          </ul>
        )}

        {comparison.worstPerValve.length > 0 && (
          <div className="flare__apply">
            <button
              type="button"
              className="btn"
              onClick={() =>
                onApplyBackpressure(
                  comparison.worstPerValve.map((w) => ({
                    tag: w.tag,
                    backpressureKpaGauge: w.backpressureKpaGauge,
                  })),
                )
              }
            >
              최악 배압을 케이스 검토로 보내기
            </button>
            <span className="muted">
              태그가 같은 밸브의 <b>모든 시나리오</b> 배압 칸을 위 '최악 배압'
              값으로 채웁니다. 배압이 바뀌면 밸브 크기가 달라지므로, 보낸 뒤
              케이스 검토에서 다시 사이징해 보십시오.
            </span>
          </div>
        )}
      </section>

      {!r.ok && (
        <section className="card">
          <div className="result result--error" role="alert">
            {r.message}
          </div>
        </section>
      )}

      {/* ── 지금 보는 조합의 결과 ── */}
      {r.ok && (
        <section className="card">
          <h2>{active?.name} — 계산 결과</h2>
          <div className="result result--ok">
            <div className="result__headline">
              <span className="result__label">이 조합의 최고 배압</span>
              <strong className="result__value">
                {r.worstValve?.tag ?? "—"} ·{" "}
                {nf(r.worstValve?.backpressureKpaGauge ?? Number.NaN, 1)} kPag
              </strong>
            </div>
            <dl className="result__grid">
              <Item label="총 유량" value={`${nf(r.totalFlowKgH, 0)} kg/h`} />
              <Item label="분출 밸브" value={`${r.valves.length} 개`} />
              <Item
                label="헤더 최대 마하수"
                value={nf(Math.max(...r.segments.map((s) => s.outletMach)), 3)}
              />
            </dl>
          </div>

          <h3>밸브별 배압</h3>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>태그</th>
                  <th>유량 (kg/h)</th>
                  <th>합류 구간</th>
                  <th>합류점 압력 (kPa abs)</th>
                  <th>테일 강하 (kPa)</th>
                  <th>총 배압 (kPag)</th>
                  <th>테일 Ma</th>
                </tr>
              </thead>
              <tbody>
                {r.valves.map((v) => (
                  <tr
                    key={v.id}
                    className={
                      v.id === r.worstValve?.id ? "row--selected" : undefined
                    }
                  >
                    <td>{v.tag}</td>
                    <td>{nf(v.massFlowKgH, 0)}</td>
                    <td className="muted">{segmentLabel(v.segmentId)}</td>
                    <td>{nf(v.nodePressureKpaAbs, 1)}</td>
                    <td>{nf(v.tailDropKpa, 1)}</td>
                    <td>
                      <b>{nf(v.backpressureKpaGauge, 1)}</b>
                    </td>
                    <td className={v.machExceeded ? "cell--bad" : undefined}>
                      {nf(v.tailOutletMach, 3)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3>헤더 구간 (팁에 가까운 것부터)</h3>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>구간</th>
                  <th>흘러가는 곳</th>
                  <th>누적 유량 (kg/h)</th>
                  <th>내경 (mm)</th>
                  <th>입구 → 출구 (kPa abs)</th>
                  <th>강하 (kPa)</th>
                  <th>출구 Ma</th>
                  <th>임계압 (kPa abs)</th>
                  <th>혼합 M</th>
                </tr>
              </thead>
              <tbody>
                {r.segments.map((s) => {
                  const spec = value.segments.find((x) => x.id === s.id);
                  return (
                    <tr key={s.id}>
                      <td>
                        {"　".repeat(s.depth)}
                        {s.label}
                      </td>
                      <td className="muted">
                        {segmentLabel(spec?.downstreamId ?? null)}
                      </td>
                      <td>{nf(s.massFlowKgH, 0)}</td>
                      <td>{nf(s.innerDiameterM * 1000, 1)}</td>
                      <td>
                        {nf(s.inletPressureKpaAbs, 1)} →{" "}
                        {nf(s.outletPressureKpaAbs, 1)}
                      </td>
                      <td>{nf(s.pressureDropKpa, 1)}</td>
                      <td className={s.machExceeded ? "cell--bad" : undefined}>
                        {nf(s.outletMach, 3)}
                      </td>
                      <td>{nf(s.criticalPressureKpaAbs, 1)}</td>
                      <td>{nf(s.molecularWeight, 1)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {r.warnings.length > 0 && (
            <ul className="warnings">
              {r.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* ── 밸브 사양 ── */}
      <section className="card">
        <div className="row-head">
          <h2>밸브 사양</h2>
          <button
            type="button"
            className="btn"
            disabled={value.segments.length === 0}
            onClick={() =>
              patch({
                valves: [
                  ...value.valves,
                  newFlareValve(
                    `PSV-${101 + value.valves.length}`,
                    value.segments[0]!.id,
                  ),
                ],
              })
            }
          >
            + 밸브 추가
          </button>
        </div>
        <p className="hint">
          물성과 테일파이프는 <b>조합이 바뀌어도 그대로</b>입니다. 유량은 위
          '동시 분출 조합' 표에서 조합별로 넣으십시오.
        </p>

        {value.valves.map((v) => {
          const res = r.ok ? r.valves.find((x) => x.id === v.id) : undefined;
          const d = derivedOf(v.id);
          return (
            <div className="scenario" key={v.id}>
              <div className="scenario__head">
                <input
                  className="flare__tag"
                  value={v.tag}
                  onChange={(e) => patchValve(v.id, { tag: e.target.value })}
                />
                <label className="flare__node">
                  합류 구간
                  <Help topic="flareNode" />
                  <select
                    value={v.segmentId}
                    onChange={(e) =>
                      patchValve(v.id, { segmentId: e.target.value })
                    }
                  >
                    {value.segments.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() =>
                    patch({
                      valves: value.valves.filter((x) => x.id !== v.id),
                      // 조합에 남은 유량도 함께 지운다.
                      scenarios: value.scenarios.map((sc) => {
                        const flows = { ...sc.flows };
                        delete flows[v.id];
                        return { ...sc, flows };
                      }),
                    })
                  }
                >
                  삭제
                </button>
              </div>

              <div className="grid-form">
                <Field label="분자량" unit="kg/kmol" help="molecularWeight">
                  <input
                    value={v.molecularWeight}
                    onChange={(e) =>
                      patchValve(v.id, { molecularWeight: e.target.value })
                    }
                  />
                </Field>
                <Field label="배출 온도" unit="K" help="temperatureK">
                  <input
                    value={v.temperatureK}
                    onChange={(e) =>
                      patchValve(v.id, { temperatureK: e.target.value })
                    }
                  />
                </Field>
                <Field label="압축계수 Z" unit="" help="compressibility">
                  <input
                    value={v.compressibility}
                    onChange={(e) =>
                      patchValve(v.id, { compressibility: e.target.value })
                    }
                  />
                </Field>
                <Field label="점도" unit="cP" help="flareViscosity">
                  <input
                    value={v.viscosityCp}
                    onChange={(e) =>
                      patchValve(v.id, { viscosityCp: e.target.value })
                    }
                  />
                </Field>
              </div>

              <h4 className="flare__sub">테일파이프</h4>
              <PipeSpecForm
                value={v.tail}
                onChange={(tail) => patchValve(v.id, { tail })}
              />

              {d && (
                <p className="hint">
                  내경 {nf(d.innerDiameterMm, 1)} mm · 등가길이{" "}
                  {nf(d.equivalentLengthM, 1)} m · 조도 {nf(d.roughnessMm, 4)} mm
                  {res &&
                    ` · Re ${res.reynolds.toExponential(2)} · f ${res.frictionFactor.toFixed(4)}` +
                      ` · 이 조합에서 배압 ${nf(res.backpressureKpaGauge, 1)} kPag`}
                </p>
              )}
            </div>
          );
        })}
      </section>

      {/* ── 헤더 구간 ── */}
      <section className="card">
        <div className="row-head">
          <h2>헤더 구간</h2>
          <button
            type="button"
            className="btn"
            onClick={() =>
              patch({
                segments: [
                  ...value.segments,
                  newFlareSegment(
                    `구간 ${value.segments.length + 1}`,
                    // 새 구간은 팁에 닿는 구간으로 흘려보낸다.
                    value.segments.find((s) => s.downstreamId === null)?.id ??
                      null,
                  ),
                ],
              })
            }
          >
            + 구간 추가
          </button>
        </div>
        <p className="hint">
          구간마다 <b>어디로 흘러 나가는지</b>를 지정해 망을 만듭니다.
          <b> 플레어 팁</b>을 고른 구간이 팁에 닿으며, 그런 구간은 하나뿐이어야
          합니다. 관경이 바뀌는 곳마다 구간을 나누십시오 (5.5.9).
        </p>

        {value.segments.map((seg) => {
          const res = r.ok ? r.segments.find((x) => x.id === seg.id) : undefined;
          const d = derivedOf(seg.id);
          return (
            <div className="scenario" key={seg.id}>
              <div className="scenario__head">
                <input
                  className="flare__tag"
                  value={seg.label}
                  onChange={(e) =>
                    patch({
                      segments: value.segments.map((x) =>
                        x.id === seg.id ? { ...x, label: e.target.value } : x,
                      ),
                    })
                  }
                />
                <label className="flare__node">
                  흘러가는 곳
                  <Help topic="flareDownstream" />
                  <select
                    value={seg.downstreamId ?? ""}
                    onChange={(e) =>
                      patch({
                        segments: value.segments.map((x) =>
                          x.id === seg.id
                            ? {
                                ...x,
                                downstreamId:
                                  e.target.value === "" ? null : e.target.value,
                              }
                            : x,
                        ),
                      })
                    }
                  >
                    <option value="">플레어 팁 (직결)</option>
                    {value.segments
                      .filter((s) => s.id !== seg.id)
                      .map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.label}
                        </option>
                      ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="btn btn--ghost"
                  disabled={value.segments.length <= 1}
                  onClick={() => {
                    const rest = value.segments.filter((x) => x.id !== seg.id);
                    const fallback = rest[0]!.id;
                    patch({
                      // 이 구간을 가리키던 연결은 그 하류로 잇는다.
                      segments: rest.map((x) =>
                        x.downstreamId === seg.id
                          ? { ...x, downstreamId: seg.downstreamId }
                          : x,
                      ),
                      // 이 구간에 붙어 있던 밸브는 남은 첫 구간으로 옮긴다.
                      valves: value.valves.map((v) =>
                        v.segmentId === seg.id
                          ? { ...v, segmentId: fallback }
                          : v,
                      ),
                    });
                  }}
                >
                  삭제
                </button>
              </div>

              <PipeSpecForm
                value={seg.pipe}
                onChange={(pipe) =>
                  patch({
                    segments: value.segments.map((x) =>
                      x.id === seg.id ? { ...x, pipe } : x,
                    ),
                  })
                }
              />

              {d && (
                <p className="hint">
                  내경 {nf(d.innerDiameterMm, 1)} mm · 등가길이{" "}
                  {nf(d.equivalentLengthM, 1)} m · 조도 {nf(d.roughnessMm, 4)} mm
                  {res &&
                    ` · Re ${res.reynolds.toExponential(2)} · f ${res.frictionFactor.toFixed(4)}` +
                      ` · 강하 ${nf(res.pressureDropKpa, 1)} kPa · Ma ${nf(res.outletMach, 3)}`}
                </p>
              )}
            </div>
          );
        })}
      </section>
    </>
  );
}
